'use strict';

import { BaseElement } from './BaseElement';
import { changeLangToCurrent, getCurrentLang } from '../components/langSwitcher/langSwitcher';
import { isMobile } from '../../settings';

export class MultipleSelector {
	static scopes = {};
	
	constructor (select, id, params = {
		animate: 'fade',
		minimumCountSelected: 1000
	}) {
		this.selectHTML = select;
		this.id = id;
		this.callbacks = {};
		this.selected = new Set();
		this.placeholder = null;
		this.counter = null;
		this.closeIcon = null;
		this.minSelected = 2;
		
		const scope = this;
		
		this.options = {
			...params,
			onCheckAll: function() {
				this.data.forEach(obj => {
					if (obj.type === 'optgroup') {
						obj.children.forEach(b => {
							scope.selected.add(b._data);
						});
					} else {
						scope.selected.add(obj._data);
					}
				});
				
				if (scope.callbacks['onCheckAll']) {
					scope.callbacks['onCheckAll'](this);
				}
				
				scope.#updateCloseIcon();
				scope.#updatePlaceholder();
				scope.#updateCounter();
			},
			onOpen: function() {
				if (isMobile) {
					const dropdown = scope.selectHTML.parentElement.querySelector('.ms-drop');
					if (dropdown) {
						const list = dropdown.querySelector('ul');
						const originSize = 250;
						const bounds = list.getBoundingClientRect();
						const listSize = bounds.y + bounds.height;
						const screenSize = window.innerHeight - 65;
						
						if (listSize > screenSize) {
							list.style.maxHeight = originSize - (listSize - screenSize) + 'px';
						} else {
							list.style.maxHeight = originSize + 'px';
						}
					}
				}
			},
			onUncheckAll: function() {
				this.data.forEach(obj => {
					if (obj.type === 'optgroup') {
						obj.children.forEach(b => {
							scope.selected.delete(b._data);
						});
					} else {
						scope.selected.delete(obj._data);
					}
				});
				
				if (scope.callbacks['onUncheckAll']) {
					scope.callbacks['onUncheckAll'](this);
				}
				
				scope.#updateCloseIcon();
				scope.#updatePlaceholder();
				scope.#updateCounter();
			},
			onOptgroupClick: function(view) {
				if (view.selected) {
					view.children.forEach(b => {
						scope.selected.add(b.data);
					});
				} else {
					view.children.forEach(b => {
						scope.selected.delete(b.data);
					});
				}
				
				if (scope.callbacks['onOptgroupClick']) {
					scope.callbacks['onOptgroupClick'](this, view);
				}
				
				scope.#updateCloseIcon();
				scope.#updatePlaceholder();
				scope.#updateCounter();
			},
			onClick: function(view) {
				if (view.selected) {
					scope.selected.add(view.data);
				} else {
					scope.selected.delete(view.data);
				}
				
				if (scope.callbacks['onClick']) {
					scope.callbacks['onClick'](this, view);
				}
				
				scope.#updateCloseIcon();
				scope.#updatePlaceholder();
				scope.#updateCounter();
			},
			onAfterCreate: function() {
				const selectBtn = scope.selectHTML.parentElement.querySelector('.ms-choice');
				
				const placeholderWrapper = new BaseElement()
					.addClass('multiple__select__placeholder-wrapper')
					.appendTo(selectBtn)
					.DOMElement;
				
				scope.placeholder = new BaseElement()
					.addClass('multiple__select__placeholder')
					.appendTo(placeholderWrapper);
				
				scope.counter = new SelectedCounter(selectBtn,scope.minSelected);
				
				
				const closeIcon = scope.selectHTML.parentElement.querySelector('.icon-close');
				if (closeIcon) {
					scope.closeIcon = closeIcon;
				}
				
				if (scope.callbacks['onAfterCreate']) {
					scope.callbacks['onAfterCreate'](this);
				}
				
				setTimeout(() => {
					scope.#updateCloseIcon();
					scope.#updatePlaceholder();
					scope.#updateCounter();
				},0);
			},
		};
		
		this.selector = $(this.selectHTML).multipleSelect(this.options);
		
		MultipleSelector.scopes[this.id] = this;
	}
	
	setSelect(value) {
		const parent = this.selectHTML.parentElement;
		const input = parent.querySelector(`input[type="checkbox"][value="${value}"]`);
		input && input.click();
	}
	
	#getLastSelectedElements() {
		return [...this.selected].slice(this.selected.size - this.minSelected, this.selected.size);
	}
	
	#updatePlaceholder() {
		if (this.placeholder) {
			if (this.callbacks['onUpdatePlaceholder']) {
				this.callbacks['onUpdatePlaceholder'](this.placeholder,this.selected);
			} else {
				const lastElements = this.#getLastSelectedElements();
				if (lastElements.length) {
					const data = lastElements.map(el => JSON.stringify(el));
					if (data.length > 1) {
						this.placeholder.innerHtml(data.join(' | '));
					} else {
						this.placeholder.innerHtml(data.join(''));
					}
				} else {
					this.placeholder.innerHtml('');
				}
			}
		}
	}
	
	#updateCounter() {
		if (this.counter) {
			this.counter.minSelected = this.minSelected;
			this.counter.count(this.selected.size);
		}
	}
	
	#updateCloseIcon() {
		if (this.closeIcon) {
			if (this.selected.size === 0) {
				this.closeIcon.classList.add('hidden');
			} else {
				this.closeIcon.classList.remove('hidden');
			}
		}
	}
	
	addCallback (event, listener) {
		this.callbacks[event] = listener;
	}
	
	refresh() {
		if (this.callbacks['onLanguageUpdate']) {
			this.callbacks['onLanguageUpdate'](this.selectHTML,this.selected);
		} else {
			const currentLang = getCurrentLang();
			const elements = this.selectHTML.querySelectorAll('option');
			[...elements].forEach(el => {
				if (el.getAttribute('data-dictionary')) {
					el.innerHTML = changeLangToCurrent(el.getAttribute('data-dictionary'));
				} else if (el.getAttribute(`data-lang-${currentLang}`)) {
					el.innerHTML = el.getAttribute(`data-lang-${currentLang}`);
				}
			});
		}
		
		setTimeout(() => {
			this.selector.multipleSelect('refresh');
		},100);
	}
	
	dispose () {
		this.selector.multipleSelect('destroy');
		this.selector = null;
		this.callbacks = null;
		this.selectHTML.remove();
		this.selectHTML = null;
		this.options = null;
		this.placeholder = null;
		this.id = null;
		this.selected = null;
		this.placeholder = null;
		this.counter = null;
		this.closeIcon = null;
	}
	
	uncheckAll () {
		this.selector.multipleSelect('uncheckAll');
	}
	
	static refresh() {
		Object.values(MultipleSelector.scopes).forEach(scope => scope.refresh());
	}
	
	static uncheckAll (id) {
		const scope = MultipleSelector.getScope(id);
		if (scope) {
			scope.uncheckAll();
		}
	}
	
	static getScope (id) {
		return MultipleSelector.scopes[id];
	}
	
	static dispose (id) {
		const scope = MultipleSelector.getScope(id);
		if (scope) {
			scope.dispose();
			delete MultipleSelector.scopes[id];
		}
	}
}


class SelectedCounter {
	constructor (parent,minSelected) {
		// this.placeholder = placeholder;
		this.minSelected = minSelected;
		this.main = new BaseElement()
			.addClass('multiple__select__counter-main')
			.appendTo(parent)
			.DOMElement;
		
		new BaseElement()
			.addClass('multiple__select__selected-icon','icon-waterfall')
			.appendTo(this.main);
		
		this.counter = new BaseElement('span')
			.addClass('multiple__select__selected-counter')
			.appendTo(this.main);
	}
	
	count(value) {
		if (value > this.minSelected) {
			this.main.classList.remove('hidden');
			this.counter.innerHtml(value);
		} else {
			this.main.classList.add('hidden');
		}
		// const scroll = this.placeholder.DOMElement.scrollWidth;
		// const width = this.placeholder.DOMElement.getBoundingClientRect().width;
		// if (scroll - 10 > width) {
		// 	this.main.classList.remove('hidden');
		// 	this.counter.innerHtml(value);
		// } else {
		// 	this.main.classList.add('hidden');
		// }
	}
	
}


window.ms = MultipleSelector;
